// THE synthetic-traffic marker. One constant, one place.
//
// Every caller that hits production (or a candidate, which shares production's
// backend) but is NOT a customer — the Jest HTTP e2e suites, the deploy-gate
// catalogue sweep, the Playwright specs, the render/sitemap scripts — MUST send
// this User-Agent (or a UA that contains SYNTHETIC_UA_TOKEN). It is the only
// discriminator the checkout-failure monitoring has: without it a sweep that
// prices every product in every region is indistinguishable from customers in
// the logs, and the 499 / 5xx / proxy-failure alert policies measure our own
// testing (ops/monitoring/README.md, "Contamination").
//
// How it reaches the logs:
//   - Cloud Run request log (`httpRequest.userAgent`)  <- sent by the caller.
//   - App Engine request log (`protoPayload.userAgent`) <- FORWARDED by the
//     website's API routes: utils/http.js `get`/`post` take `opts.userAgent`, and
//     utils/appRouteRequest.js `backendCallOpts(request, opts)` supplies it when
//     (and only when) the inbound request is synthetic. Customers keep arriving
//     at the backend as axios's default UA, exactly as before.
//   - Cloud Run stderr (`textPayload`, the `[backend] ... failed:` /
//     `[backend-slow]` / `[makeOrderAndStartPayment] backend ...` lines the
//     log-based metrics consume) has NO httpRequest field at all, so those lines
//     carry SYNTHETIC_LOG_MARKER as a trailing token instead.
//
// Plain CommonJS on purpose: it is required from Jest (babel), imported from the
// App Router (Next), imported from ESM `.mjs` scripts (Node's CJS named-export
// detection handles the static `module.exports = { ... }` below), and read by
// playwright.config.js. Keep it dependency-free.

const SYNTHETIC_USER_AGENT = 'VRS-Synthetic/1.0 (+deploy-gate)'

// The substring the metric filters key on. A version bump of the constant above
// must keep this prefix, or every exclusion in ops/monitoring/metrics/*.json
// silently stops matching.
const SYNTHETIC_UA_TOKEN = 'VRS-Synthetic/'

// Trailing token appended to the stderr log lines the textPayload metrics read.
// Filters exclude with `NOT textPayload:"caller=synthetic"`.
const SYNTHETIC_LOG_MARKER = 'caller=synthetic'

function isSyntheticUserAgent(ua) {
  return typeof ua === 'string' && ua.includes(SYNTHETIC_UA_TOKEN)
}

// `syntheticUserAgent('cache-e2e-test')` -> 'VRS-Synthetic/1.0 (+deploy-gate) cache-e2e-test'
// Keeps a suite's identity visible in the access logs while still carrying the
// marker. A browser UA can carry it as a suffix the same way (playwright.config.js).
function syntheticUserAgent(tag) {
  return tag ? `${SYNTHETIC_USER_AGENT} ${tag}` : SYNTHETIC_USER_AGENT
}

// Read the inbound User-Agent off a Web `Request` (App Router), a Node `req`
// (`req.headers['user-agent']`) or a plain header map, and return it ONLY if it
// is synthetic. Returns undefined for customers, so callers can spread the
// result straight into backend-call options without leaking real browser UAs.
function syntheticUserAgentFrom(requestOrHeaders) {
  if (!requestOrHeaders) return undefined
  let ua
  const h = requestOrHeaders.headers || requestOrHeaders
  if (h && typeof h.get === 'function') ua = h.get('user-agent')
  else if (h && typeof h === 'object') ua = h['user-agent']
  return isSyntheticUserAgent(ua) ? ua : undefined
}

module.exports = {
  SYNTHETIC_USER_AGENT,
  SYNTHETIC_UA_TOKEN,
  SYNTHETIC_LOG_MARKER,
  isSyntheticUserAgent,
  syntheticUserAgent,
  syntheticUserAgentFrom,
}
